import { useState } from "react";
import { api } from "@/lib/api";
import { CATEGORIES, catLabel, regionLabel, t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkle, Check, X, Trash, Image as ImageIcon } from "@phosphor-icons/react";

const empty = { no: "", es: "", en: "", pl: "" };

export function AdminWordRow({ word, onChanged, lang }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    word: word.word,
    category: word.category,
    region: word.region,
    source_lang: word.source_lang || "no",
    definition: { ...empty, ...(word.definition || {}) },
    example: { ...empty, ...(word.example || {}) },
    translations: { ...empty, ...(word.translations || {}) },
  });

  async function save(extraPatch = {}) {
    setBusy(true);
    try {
      await api.patch(`/admin/words/${word.id}`, { ...form, ...extraPatch });
      toast.success("Saved");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  }

  async function regen(sourceLang = "es") {
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/regenerate-translations/${word.id}?source_lang=${sourceLang}`,
      );
      setForm((f) => ({
        ...f,
        definition: { ...empty, ...r.data.definition },
        example: { ...empty, ...r.data.example },
        translations: { ...r.data.translations },
      }));
      toast.success(`Re-localized from ${sourceLang.toUpperCase()}`);
      onChanged();
    } catch {
      toast.error("AI error");
    } finally {
      setBusy(false);
    }
  }

  async function regenOg() {
    setBusy(true);
    try {
      await api.delete(`/admin/og/${word.id}`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/og/${word.id}.png?t=${Date.now()}`;
      await fetch(url, { cache: "reload" });
      toast.success("OG card regenerated");
    } catch {
      toast.error("OG error");
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!window.confirm("Delete?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/words/${word.id}`);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`admin-row-${word.id}`}
      className="bg-white border border-gray-200 rounded-2xl p-7"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <RowTitle word={word} editing={editing} form={form} setForm={setForm} lang={lang} />
        <RowActions
          word={word}
          editing={editing}
          setEditing={setEditing}
          busy={busy}
          save={save}
          regenOg={regenOg}
          del={del}
          lang={lang}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <LocalizedColumn
          label={t("definition_label", lang)}
          editing={editing}
          field="definition"
          form={form}
          setForm={setForm}
          display={word.definition}
          lang={lang}
        />
        <LocalizedColumn
          label={t("example_label", lang)}
          editing={editing}
          field="example"
          form={form}
          setForm={setForm}
          display={word.example}
          lang={lang}
          italic
        />
      </div>

      <TranslationsSection
        editing={editing}
        busy={busy}
        regen={regen}
        form={form}
        setForm={setForm}
        word={word}
        lang={lang}
      />

      {editing && (
        <div className="mt-5 flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => save()}
            disabled={busy}
            className="bg-gray-900 hover:bg-gray-800 rounded-full"
          >
            {t("save", lang)}
          </Button>
        </div>
      )}
    </div>
  );
}

function RowTitle({ word, editing, form, setForm, lang }) {
  return (
    <div className="flex-1">
      {editing ? (
        <Input
          value={form.word}
          onChange={(e) => setForm({ ...form, word: e.target.value })}
          className="font-serif-display text-3xl font-bold py-2"
        />
      ) : (
        <h3 className="font-serif-display text-3xl font-bold tracking-tighter">{word.word}</h3>
      )}
      <div className="flex items-center gap-3 mt-2">
        {editing ? (
          <Select
            value={form.source_lang}
            onValueChange={(v) => setForm({ ...form, source_lang: v })}
          >
            <SelectTrigger data-testid={`edit-source-${word.id}`} className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="no">🇳🇴 Norsk</SelectItem>
              <SelectItem value="es">🇪🇸 Español</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span
            data-testid={`row-source-${word.id}`}
            className={`text-[10px] tracking-eyebrow font-medium px-2 py-0.5 rounded-full ${
              word.source_lang === "es"
                ? "bg-[#FBE9E7] text-[#C1121F]"
                : "bg-[#E8F0FE] text-[#1a56b0]"
            }`}
          >
            {word.source_lang === "es" ? "🇪🇸 Español" : "🇳🇴 Norsk"}
          </span>
        )}
        {editing ? (
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {catLabel(c, lang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-[10px] tracking-eyebrow text-gray-500">
            {catLabel(word.category, lang)}
          </span>
        )}
        {editing ? (
          <Input
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            placeholder="Region"
            className="h-8 w-40 text-xs"
          />
        ) : (
          word.region && (
            <span className="text-[10px] font-mono-code text-gray-400 uppercase">
              · {regionLabel(word.region, lang)}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function RowActions({ word, editing, setEditing, busy, save, regenOg, del, lang }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {word.status === "pending" && (
        <>
          <Button
            size="sm"
            data-testid={`approve-${word.id}`}
            onClick={() => save({ status: "approved" })}
            disabled={busy}
            className="bg-[#2A9D8F] hover:bg-[#22867A] rounded-full"
          >
            <Check size={14} className="mr-1" /> {t("approve", lang)}
          </Button>
          <Button
            size="sm"
            data-testid={`reject-${word.id}`}
            variant="outline"
            onClick={() => save({ status: "rejected" })}
            disabled={busy}
            className="rounded-full"
          >
            <X size={14} className="mr-1" /> {t("reject", lang)}
          </Button>
        </>
      )}
      {word.status === "rejected" && (
        <Button
          size="sm"
          onClick={() => save({ status: "approved" })}
          disabled={busy}
          className="bg-[#2A9D8F] hover:bg-[#22867A] rounded-full"
        >
          {t("approve", lang)}
        </Button>
      )}
      {word.status === "approved" && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => save({ status: "rejected" })}
          disabled={busy}
          className="rounded-full"
        >
          {t("reject", lang)}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        data-testid={`edit-${word.id}`}
        onClick={() => setEditing(!editing)}
        className="rounded-full"
      >
        {editing ? "—" : "Edit"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={regenOg}
        disabled={busy}
        title="Regenerate OG card"
        data-testid={`regen-og-${word.id}`}
        className="rounded-full text-xs"
      >
        <ImageIcon size={14} className="mr-1" /> OG
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={del}
        disabled={busy}
        className="text-red-600 hover:bg-red-50"
      >
        <Trash size={14} />
      </Button>
    </div>
  );
}

function LocalizedColumn({ label, editing, field, form, setForm, display, lang, italic }) {
  return (
    <div>
      <p className="text-[10px] tracking-eyebrow text-gray-500 mb-1">{label}</p>
      {editing ? (
        <div className="space-y-2">
          {["no", "es", "en", "pl"].map((l) => (
            <div key={l} className="flex items-start gap-2">
              <span className="font-mono-code text-[10px] uppercase text-gray-400 pt-2 w-6">{l}</span>
              <Textarea
                value={form[field]?.[l] || ""}
                onChange={(e) =>
                  setForm({ ...form, [field]: { ...form[field], [l]: e.target.value } })
                }
                rows={2}
                className="text-sm flex-1"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className={`text-sm text-gray-700${italic ? " italic" : ""}`}>
          {(display && (display[lang] || display.no || display.es || display.en || display.pl)) || "—"}
        </p>
      )}
    </div>
  );
}

function TranslationsSection({ editing, busy, regen, form, setForm, word, lang }) {
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-eyebrow text-gray-500">
          AI · {t("translations_label", lang)}
        </p>
        <div className="flex items-center gap-1">
          {["no", "es", "en", "pl"].map((l) => (
            <Button
              key={l}
              size="sm"
              variant="outline"
              onClick={() => regen(l)}
              disabled={busy}
              title={`Re-localize using ${l.toUpperCase()} as source`}
              className="rounded-full text-xs h-8 px-3"
              data-testid={`regen-${l}-${word.id}`}
            >
              <Sparkle size={12} className="mr-1" /> {l.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {["no", "es", "en", "pl"].map((l) => (
          <div key={l} className="flex items-start gap-3">
            <span className="font-mono-code text-[10px] uppercase text-gray-400 pt-2 w-6">{l}</span>
            {editing ? (
              <Textarea
                value={form.translations?.[l] || ""}
                onChange={(e) =>
                  setForm({ ...form, translations: { ...form.translations, [l]: e.target.value } })
                }
                rows={2}
                className="text-sm flex-1"
              />
            ) : (
              <p className="text-sm text-gray-700 flex-1">{form.translations?.[l] || "—"}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
